import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import StatsDateSelector, { getMinAllowedDate } from "./StatsDateSelector";
import { applyTimeZone, fetchAPI, isAssaigDeMusics, postAPI } from "../../utils/utils";
import { requestMissingProvesBatch } from "./statsProvesData";

const DAYS = ['Dl', 'Dt', 'Dc', 'Dj', 'Dv', 'Ds', 'Dg'];
const MONTHS = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];

// Group events by week (ISO week number) and find which days have events
const groupByWeek = (events) => {
    const weeks = {};
    const daysWithEvents = new Set();
    
    events.forEach(({ event, count }) => {
        const date = moment(event['data-esperada-inici']);
        const weekKey = date.format('GGGG-WW'); // ISO year + week
        const dayOfWeek = (date.isoWeekday() - 1); // 0 = Monday
        const isActuacio = event.tipus === 'actuació';
        
        daysWithEvents.add(dayOfWeek);
        
        if (!weeks[weekKey]) {
            weeks[weekKey] = {
                weekStart: date.clone().startOf('isoWeek'),
                days: Array(7).fill(null)
            };
        }
        
        // If there's already an event on this day, combine counts
        if (weeks[weekKey].days[dayOfWeek]) {
            weeks[weekKey].days[dayOfWeek].count += count;
            weeks[weekKey].days[dayOfWeek].events.push(event);
            // If any event is actuació, mark the day as having actuació
            if (isActuacio) {
                weeks[weekKey].days[dayOfWeek].hasActuacio = true;
            }
        } else {
            weeks[weekKey].days[dayOfWeek] = { count, events: [event], hasActuacio: isActuacio };
        }
    });
    
    // Sort days that have events
    const activeDays = [...daysWithEvents].sort((a, b) => a - b);
    
    const weeksList = Object.entries(weeks)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, data]) => data);
    
    return { weeks: weeksList, activeDays };
};

const getBundleId = prova => prova.split('.')[0];

const normalize = str => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const PART_COLORS = {
    pinya: '#3b82f6',
    folre: '#f59e0b',
    manilles: '#10b981',
    puntals: '#8b5cf6',
    tronc: '#64748b'
};

const getBundleParts = (bundle) => {
    if (!bundle?.parts) return [];
    return Object.keys(bundle.parts)
        .map(part => part.toLowerCase())
        .filter(part => ['pinya', 'folre', 'manilles', 'puntals', 'tronc'].includes(part));
};

// Get unique plantilles from a bundle (excluding Organització)
const getBundlePlantilles = (bundle) => {
    if (!bundle?.parts) return [];
    return [...new Set(
        Object.entries(bundle.parts)
            .filter(([part]) => part.toLowerCase() !== 'organització')
            .map(([, plantilla]) => plantilla)
            .filter(Boolean)
    )];
};

function StatsProvesTab({ events }) {
    const minAllowedDate = getMinAllowedDate();
    const [start_date, setStart_date] = useState(minAllowedDate);
    const [end_date, setEnd_date] = useState(moment().format('YYYY-MM-DD'));
    const [bundles, setBundles] = useState([]);
    const [provesByEvent, setProvesByEvent] = useState({});
    const [hiddenEventIds, setHiddenEventIds] = useState([]);
    const [hiddenPlantilles, setHiddenPlantilles] = useState([]);
    const [plantillaLimit, setPlantillaLimit] = useState(10);
    const [bundleLimit, setBundleLimit] = useState(10);
    const [hoveredPlantilla, setHoveredPlantilla] = useState(null);
    const [hoveredBundleId, setHoveredBundleId] = useState(null);
    const [filterBundleId, setFilterBundleId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const pendingProvesEventIds = useRef(new Set());

    useEffect(() => {
        fetchAPI('/bundles', setBundles);
    }, []);

    const bundlesById = useMemo(() => {
        return bundles.reduce((acc, bundle) => {
            acc[bundle.id] = bundle;
            return acc;
        }, {});
    }, [bundles]);

    const eventsInRange = useMemo(() => {
        return events
            .filter(event => event.tipus === 'assaig' || event.tipus === 'actuació')
            .filter(event => !isAssaigDeMusics(event))
            // Ignora actuacions comercials
            .filter(event => !(event.tipus === 'actuació' && event.title?.toLowerCase().includes('comercial')))
            .filter(event => moment(applyTimeZone(event['data-esperada-inici'])).isBetween(start_date, end_date, 'day', '[]'))
            .filter(event => !hiddenEventIds.includes(event.id))
            .sort((a, b) => moment(a['data-esperada-inici']).diff(moment(b['data-esperada-inici'])));
    }, [events, start_date, end_date, hiddenEventIds]);

    useEffect(() => {
        const eventIds = eventsInRange.map(event => event.id);
        if (eventIds.length === 0) {
            return;
        }

        requestMissingProvesBatch({
            eventIds,
            provesByEvent,
            pendingEventIds: pendingProvesEventIds.current,
            postAPI,
            onLoaded: loadedProves => {
                setProvesByEvent(previous => ({ ...previous, ...loadedProves }));
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsInRange]);

    const eventsWithProves = useMemo(() => {
        return eventsInRange.filter(event => (provesByEvent[event.id] || []).length > 0);
    }, [eventsInRange, provesByEvent]);

    const provaRanking = useMemo(() => {
        const stats = {};

        // Build stats from events that have proves
        eventsWithProves.forEach(event => {
            const proves = provesByEvent[event.id] || [];
            proves.forEach(prova => {
                const bundleId = getBundleId(prova);
                
                if (!stats[bundleId]) {
                    stats[bundleId] = {
                        count: 0,
                        eventCounts: {} // { eventId: count }
                    };
                }

                stats[bundleId].count += 1;
                stats[bundleId].eventCounts[event.id] = (stats[bundleId].eventCounts[event.id] || 0) + 1;
            });
        });

        return Object.entries(stats)
            .map(([bundleId, data]) => {
                const bundle = bundlesById[bundleId];
                const label = bundle?.nom ?? bundleId;
                const parts = getBundleParts(bundle);
                // Include ALL events, with count 0 for those without this prova
                const eventsList = eventsInRange.map(event => ({
                    event,
                    count: data.eventCounts[event.id] || 0
                }));

                return {
                    bundleId,
                    label,
                    parts,
                    count: data.count,
                    events: eventsList
                };
            })
            .sort((a, b) => {
                if (b.count !== a.count) return b.count - a.count;
                return a.label.localeCompare(b.label);
            });
    }, [eventsWithProves, provesByEvent, bundlesById, eventsInRange]);

    // Map bundleId -> plantilles it uses
    const plantillesByBundle = useMemo(() => {
        const map = {};
        provaRanking.forEach(prova => {
            const bundle = bundlesById[prova.bundleId];
            const plantilles = getBundlePlantilles(bundle);
            map[prova.bundleId] = plantilles;
        });
        return map;
    }, [provaRanking, bundlesById]);

    // Group by plantilla (template)
    const plantillaRanking = useMemo(() => {
        const stats = {};

        eventsWithProves.forEach(event => {
            const proves = provesByEvent[event.id] || [];
            proves.forEach(prova => {
                const bundleId = getBundleId(prova);
                const bundle = bundlesById[bundleId];
                const plantilles = getBundlePlantilles(bundle);
                
                plantilles.forEach(plantilla => {
                    if (!stats[plantilla]) {
                        stats[plantilla] = {
                            count: 0,
                            eventCounts: {},
                            bundleCounts: {}
                        };
                    }
                    
                    stats[plantilla].count += 1;
                    stats[plantilla].eventCounts[event.id] = (stats[plantilla].eventCounts[event.id] || 0) + 1;
                    stats[plantilla].bundleCounts[bundleId] = (stats[plantilla].bundleCounts[bundleId] || 0) + 1;
                });
            });
        });

        return Object.entries(stats)
            .filter(([plantilla]) => !hiddenPlantilles.includes(plantilla))
            .map(([plantilla, data]) => {
                const eventsList = eventsInRange.map(event => ({
                    event,
                    count: data.eventCounts[event.id] || 0
                }));

                // Get bundles that use this plantilla with more than 1 event
                const bundles = Object.entries(data.bundleCounts)
                    .filter(([, count]) => count > 1)
                    .map(([bundleId, count]) => ({
                        bundleId,
                        label: bundlesById[bundleId]?.shortName || bundlesById[bundleId]?.nom || bundleId,
                        count
                    }))
                    .sort((a, b) => b.count - a.count);

                return {
                    plantilla,
                    count: data.count,
                    events: eventsList,
                    bundles
                };
            })
            .sort((a, b) => {
                if (b.count !== a.count) return b.count - a.count;
                return a.plantilla.localeCompare(b.plantilla);
            });
    }, [eventsWithProves, provesByEvent, bundlesById, eventsInRange, hiddenPlantilles]);

    // Get plantilles used by the filter bundle
    const filterPlantilles = useMemo(() => {
        if (!filterBundleId) return null;
        return plantillesByBundle[filterBundleId] || [];
    }, [filterBundleId, plantillesByBundle]);

    // Get all bundles that share any plantilla with the filter bundle
    const relatedBundleIds = useMemo(() => {
        if (!filterPlantilles) return null;
        const related = new Set();
        provaRanking.forEach(prova => {
            const bundlePlantilles = plantillesByBundle[prova.bundleId] || [];
            if (bundlePlantilles.some(p => filterPlantilles.includes(p))) {
                related.add(prova.bundleId);
            }
        });
        return related;
    }, [filterPlantilles, provaRanking, plantillesByBundle]);

    // Filtered rankings
    const filteredPlantillaRanking = useMemo(() => {
        if (!filterPlantilles) return plantillaRanking;
        return plantillaRanking.filter(item => filterPlantilles.includes(item.plantilla));
    }, [plantillaRanking, filterPlantilles]);

    const filteredProvaRanking = useMemo(() => {
        if (!relatedBundleIds) return provaRanking;
        return provaRanking.filter(prova => relatedBundleIds.has(prova.bundleId));
    }, [provaRanking, relatedBundleIds]);

    // Search results for autocomplete
    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) return [];
        const normalizedSearch = normalize(searchTerm);
        return provaRanking
            .filter(prova => {
                const bundle = bundlesById[prova.bundleId];
                const shortName = bundle?.shortName || '';
                return normalize(prova.label).includes(normalizedSearch) || 
                       normalize(shortName).includes(normalizedSearch);
            })
            .slice(0, 8);
    }, [searchTerm, provaRanking, bundlesById]);

    const handleSearchSelect = (bundleId) => {
        setFilterBundleId(bundleId);
        setSearchTerm('');
        setShowDropdown(false);
    };

    return (
        <div>
            <StatsDateSelector
                start_date={start_date}
                setStart_date={setStart_date}
                end_date={end_date}
                setEnd_date={setEnd_date}
            />

            {/* Search bar */}
            <div style={{ marginTop: 16, position: 'relative', maxWidth: 400 }}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchResults.length > 0) {
                            handleSearchSelect(searchResults[0].bundleId);
                        }
                        if (e.key === 'Escape') {
                            setShowDropdown(false);
                            setSearchTerm('');
                        }
                    }}
                    placeholder="Busca un castell per filtrar..."
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: 14,
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        outline: 'none'
                    }}
                />
                {showDropdown && searchResults.length > 0 ? (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        marginTop: 4,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 100,
                        maxHeight: 300,
                        overflowY: 'auto'
                    }}>
                        {searchResults.map((prova, index) => (
                            <div
                                key={prova.bundleId}
                                onMouseDown={() => handleSearchSelect(prova.bundleId)}
                                style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    background: index === 0 ? '#f1f5f9' : 'transparent',
                                    borderBottom: index === searchResults.length - 1 ? 'none' : '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <span style={{ fontWeight: 500, color: '#1e293b' }}>{prova.label}</span>
                                <span style={{ fontSize: 12, color: '#64748b' }}>{prova.count} proves</span>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            <div style={{ 
                marginTop: 16, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                minHeight: 28
            }}>
                {filterBundleId ? (
                    <button
                        onClick={() => setFilterBundleId(null)}
                        style={{
                            padding: '4px 10px',
                            fontSize: 12,
                            background: '#3b82f6',
                            border: 'none',
                            borderRadius: 4,
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }}
                    >
                        <span>Filtrant per: {bundlesById[filterBundleId]?.nom || filterBundleId}</span>
                        <span style={{ fontWeight: 400 }}>✕</span>
                    </button>
                ) : (
                    <div />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {hiddenEventIds.length > 0 ? (
                    <>
                        <span style={{
                            fontSize: 12,
                            color: '#94a3b8'
                        }}>
                            {hiddenEventIds.length} assaig{hiddenEventIds.length > 1 ? 's' : ''} amagat{hiddenEventIds.length > 1 ? 's' : ''}
                        </span>
                        <button
                            onClick={() => setHiddenEventIds([])}
                            style={{
                                padding: '4px 10px',
                                fontSize: 12,
                                background: '#3b82f6',
                                border: 'none',
                                borderRadius: 4,
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}
                        >
                            Restaurar
                        </button>
                    </>
                ) : (
                    <span style={{
                        fontSize: 12,
                        color: '#94a3b8',
                        fontStyle: 'italic'
                    }}>
                        Pica sobre un quadrat per amagar un assaig de les estadístiques
                    </span>
                )}
                </div>
            </div>

            <div style={{ 
                display: 'flex', 
                gap: 24, 
                marginTop: 32,
                flexWrap: 'wrap',
                alignItems: 'flex-start'
            }}>
                {/* Plantilla ranking - left side */}
                <section style={{ flex: 'none', width: '45%' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: 16,
                        height: 24
                    }}>
                        <h2 style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: '#334155',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            margin: 0
                        }}>
                            Per plantilla
                        </h2>
                        {hiddenPlantilles.length > 0 ? (
                            <button
                                onClick={() => setHiddenPlantilles([])}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: 10,
                                    background: '#f1f5f9',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 4,
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                +{hiddenPlantilles.length}
                            </button>
                        ) : null}
                    </div>
                    {filteredPlantillaRanking.length === 0 ? (
                        <p style={{ color: '#64748b', fontSize: 14 }}>
                            {filterBundleId ? 'Cap plantilla relacionada.' : 'No hi ha proves.'}
                        </p>
                    ) : (
                        <>
                            <div style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                overflow: 'hidden'
                            }}>
                                {filteredPlantillaRanking.slice(0, plantillaLimit).map((item, index) => {
                                    const { weeks, activeDays } = groupByWeek(item.events);
                                    const isHighlighted = hoveredPlantilla === item.plantilla || 
                                        (hoveredBundleId && plantillesByBundle[hoveredBundleId]?.includes(item.plantilla));
                                    return (
                                        <div
                                            key={item.plantilla}
                                            onMouseEnter={() => setHoveredPlantilla(item.plantilla)}
                                            onMouseLeave={() => setHoveredPlantilla(null)}
                                            style={{
                                                padding: '16px 20px',
                                                borderBottom: index === Math.min(plantillaLimit, filteredPlantillaRanking.length) - 1 ? 'none' : '1px solid #f1f5f9',
                                                background: isHighlighted ? '#f0fdf4' : 'transparent',
                                                transition: 'background 0.15s ease'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: 12,
                                                marginBottom: 8
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <span style={{
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        color: '#94a3b8',
                                                        minWidth: 28
                                                    }}>
                                                        #{index + 1}
                                                    </span>
                                                    <span style={{
                                                            fontSize: 15,
                                                            fontWeight: 600,
                                                            color: '#1e293b'
                                                        }}
                                                    >
                                                        {item.plantilla}
                                                    </span>
                                                </div>
                                                <span style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                    background: '#f1f5f9',
                                                    padding: '4px 10px',
                                                    borderRadius: 999
                                                }}>
                                                    {item.count} proves
                                                </span>
                                            </div>
                                            {item.bundles.length > 0 ? (
                                                <div style={{ 
                                                    display: 'flex', 
                                                    flexWrap: 'wrap', 
                                                    gap: 4, 
                                                    marginBottom: 8 
                                                }}>
                                                    {item.bundles.map(bundle => {
                                                        const isPillHighlighted = hoveredBundleId === bundle.bundleId || hoveredPlantilla === item.plantilla;
                                                        return (
                                                            <span
                                                                key={bundle.bundleId}
                                                                title={`${bundle.count} proves - clic per filtrar`}
                                                                onMouseEnter={() => setHoveredBundleId(bundle.bundleId)}
                                                                onMouseLeave={() => setHoveredBundleId(null)}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setFilterBundleId(filterBundleId === bundle.bundleId ? null : bundle.bundleId);
                                                                }}
                                                                style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 500,
                                                                    color: isPillHighlighted ? '#fff' : '#64748b',
                                                                    background: isPillHighlighted ? '#3b82f6' : '#f1f5f9',
                                                                    padding: '2px 6px',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                            >
                                                                {bundle.label}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            ) : null}
                                            <div style={{ overflowX: 'auto' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {/* Month labels row */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <div style={{ width: 16, marginRight: 4 }} />
                                                        {weeks.map((week, weekIdx) => {
                                                            const month = week.weekStart.month();
                                                            const prevMonth = weekIdx > 0 ? weeks[weekIdx - 1].weekStart.month() : -1;
                                                            const showMonth = month !== prevMonth;
                                                            return (
                                                                <div 
                                                                    key={weekIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 12,
                                                                        fontSize: 7,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'flex-end',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    {showMonth ? MONTHS[month] : ''}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Grid */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            gap: 2,
                                                            marginRight: 4
                                                        }}>
                                                            {activeDays.map(dayIdx => (
                                                                <div 
                                                                    key={dayIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        fontSize: 8,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end'
                                                                    }}
                                                                >
                                                                    {DAYS[dayIdx]}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {weeks.map((week, weekIdx) => (
                                                            <div 
                                                                key={weekIdx}
                                                                style={{ 
                                                                    display: 'flex', 
                                                                    flexDirection: 'column', 
                                                                    gap: 2 
                                                                }}
                                                            >
                                                                {activeDays.map(dayIdx => {
                                                                const day = week.days[dayIdx];
                                                                const hasData = day !== null;
                                                                const count = day?.count || 0;
                                                                const hasActuacio = day?.hasActuacio || false;
                                                                const tooltip = day 
                                                                    ? `${day.events.map(e => `${moment(e['data-esperada-inici']).format('DD/MM')} · ${e.title}${e.tipus === 'actuació' ? ' (actuació)' : ''}`).join('\n')} - ${count} ${count === 1 ? 'prova' : 'proves'}\n(clic per amagar)`
                                                                    : '';
                                                                
                                                                const handleClick = () => {
                                                                    if (day?.events) {
                                                                        const eventIds = day.events.map(e => e.id);
                                                                        setHiddenEventIds(prev => [...prev, ...eventIds]);
                                                                    }
                                                                };

                                                                // Golden for actuacions, green for assaigs
                                                                const activeColor = hasActuacio ? '#edd50f' : '#10b981';
                                                                
                                                                return (
                                                                    <div
                                                                        key={dayIdx}
                                                                        title={tooltip}
                                                                        onClick={hasData ? handleClick : undefined}
                                                                        style={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            borderRadius: 2,
                                                                            background: !hasData 
                                                                                ? 'transparent' 
                                                                                : count > 0 
                                                                                    ? activeColor 
                                                                                    : '#e2e8f0',
                                                                            border: 'none',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: 8,
                                                                            fontWeight: 600,
                                                                            color: count > 0 ? '#fff' : '#94a3b8',
                                                                            cursor: hasData ? 'pointer' : 'default'
                                                                        }}
                                                                    >
                                                                        {count > 1 ? count : ''}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {filteredPlantillaRanking.length > plantillaLimit ? (
                                <button
                                    onClick={() => setPlantillaLimit(prev => prev + 10)}
                                    style={{
                                        width: '100%',
                                        marginTop: 12,
                                        padding: '10px 16px',
                                        fontSize: 13,
                                        fontWeight: 500,
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 6,
                                        color: '#475569',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Carregar més ({filteredPlantillaRanking.length - plantillaLimit} restants)
                                </button>
                            ) : null}
                        </>
                    )}
                </section>

                {/* Bundle ranking - right side */}
                <section style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: 16,
                        height: 24
                    }}>
                        <h2 style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: '#334155',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            margin: 0
                        }}>
                            Per castell
                        </h2>
                    </div>
                {filteredProvaRanking.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: 14 }}>
                        {filterBundleId ? 'Cap castell relacionat.' : 'No hi ha proves dins del rang de dates seleccionat.'}
                    </p>
                ) : (
                    <>
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            overflow: 'hidden'
                        }}>
                            {filteredProvaRanking.slice(0, bundleLimit).map((prova, index) => {
                                const bundlePlantilles = plantillesByBundle[prova.bundleId] || [];
                                const isBundleHighlighted = hoveredBundleId === prova.bundleId || 
                                    (hoveredPlantilla && bundlePlantilles.includes(hoveredPlantilla));
                                return (
                                <div
                                    key={prova.bundleId}
                                    onMouseEnter={() => setHoveredBundleId(prova.bundleId)}
                                    onMouseLeave={() => setHoveredBundleId(null)}
                                    style={{
                                        padding: '16px 20px',
                                        borderBottom: index === Math.min(bundleLimit, filteredProvaRanking.length) - 1 ? 'none' : '1px solid #f1f5f9',
                                        background: isBundleHighlighted ? '#eff6ff' : 'transparent',
                                        transition: 'background 0.15s ease'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 12
                                    }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <span style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: '#94a3b8',
                                                    minWidth: 28
                                                }}>
                                                    #{index + 1}
                                                </span>
                                                <span 
                                                    onClick={() => {
                                                        setFilterBundleId(filterBundleId === prova.bundleId ? null : prova.bundleId);
                                                    }}
                                                    style={{
                                                        fontSize: 15,
                                                        fontWeight: 600,
                                                        color: '#1e293b',
                                                        cursor: 'pointer',
                                                        textDecoration: hoveredBundleId === prova.bundleId ? 'underline' : 'none'
                                                    }}
                                                >
                                                    {prova.label}
                                                </span>
                                            </div>
                                            {prova.parts.length > 0 ? (
                                                <div style={{ display: 'flex', gap: 4, marginTop: 6, marginLeft: 40 }}>
                                                    {prova.parts.map(part => (
                                                        <span
                                                            key={part}
                                                            style={{
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                color: '#fff',
                                                                background: PART_COLORS[part],
                                                                padding: '2px 6px',
                                                                borderRadius: 4,
                                                                textTransform: 'capitalize'
                                                            }}
                                                        >
                                                            {part}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        <span style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#475569',
                                            background: '#f1f5f9',
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {prova.count} proves
                                        </span>
                                    </div>
                                    <div style={{ marginTop: 16, overflowX: 'auto' }}>
                                        {(() => {
                                            const { weeks, activeDays } = groupByWeek(prova.events);
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {/* Month labels row */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <div style={{ width: 16, marginRight: 4 }} />
                                                        {weeks.map((week, weekIdx) => {
                                                            const month = week.weekStart.month();
                                                            const prevMonth = weekIdx > 0 ? weeks[weekIdx - 1].weekStart.month() : -1;
                                                            const showMonth = month !== prevMonth;
                                                            return (
                                                                <div 
                                                                    key={weekIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 12,
                                                                        fontSize: 7,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'flex-end',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    {showMonth ? MONTHS[month] : ''}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Grid */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        {/* Day labels - only for days with assaigs */}
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            gap: 2,
                                                            marginRight: 4
                                                        }}>
                                                            {activeDays.map(dayIdx => (
                                                                <div 
                                                                    key={dayIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        fontSize: 8,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end'
                                                                    }}
                                                                >
                                                                    {DAYS[dayIdx]}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {/* Weeks */}
                                                        {weeks.map((week, weekIdx) => (
                                                            <div 
                                                                key={weekIdx}
                                                                style={{ 
                                                                    display: 'flex', 
                                                                    flexDirection: 'column', 
                                                                    gap: 2 
                                                                }}
                                                            >
                                                                {activeDays.map(dayIdx => {
                                                                const day = week.days[dayIdx];
                                                                const hasData = day !== null;
                                                                const count = day?.count || 0;
                                                                const hasActuacio = day?.hasActuacio || false;
                                                                const tooltip = day 
                                                                    ? `${day.events.map(e => `${moment(e['data-esperada-inici']).format('DD/MM')} · ${e.title}${e.tipus === 'actuació' ? ' (actuació)' : ''}`).join('\n')} - ${count} ${count === 1 ? 'prova' : 'proves'}\n(clic per amagar)`
                                                                    : '';
                                                                
                                                                const handleClick = () => {
                                                                    if (day?.events) {
                                                                        const eventIds = day.events.map(e => e.id);
                                                                        setHiddenEventIds(prev => [...prev, ...eventIds]);
                                                                    }
                                                                };

                                                                // Golden for actuacions, blue for assaigs
                                                                const activeColor = hasActuacio ? '#edd50f' : '#3b82f6';
                                                                
                                                                return (
                                                                    <div
                                                                        key={dayIdx}
                                                                        title={tooltip}
                                                                        onClick={hasData ? handleClick : undefined}
                                                                        style={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            borderRadius: 2,
                                                                            background: !hasData 
                                                                                ? 'transparent' 
                                                                                : count > 0 
                                                                                    ? activeColor 
                                                                                    : '#e2e8f0',
                                                                            border: 'none',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: 8,
                                                                            fontWeight: 600,
                                                                            color: count > 0 ? '#fff' : '#94a3b8',
                                                                            cursor: hasData ? 'pointer' : 'default'
                                                                        }}
                                                                    >
                                                                        {count > 1 ? count : ''}
                                                                    </div>
                                                                );
                                                            })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )})}
                        </div>
                        {filteredProvaRanking.length > bundleLimit ? (
                            <button
                                onClick={() => setBundleLimit(prev => prev + 10)}
                                style={{
                                    width: '100%',
                                    marginTop: 12,
                                    padding: '10px 16px',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    color: '#475569',
                                    cursor: 'pointer'
                                }}
                            >
                                Carregar més ({filteredProvaRanking.length - bundleLimit} restants)
                            </button>
                        ) : null}
                    </>
                )}
                </section>
            </div>
        </div>
    );
}

export default StatsProvesTab;
